import React, { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";

/**
 * Real, approved member quotes - and nothing else.
 *
 * This component renders NOTHING until /api/testimonials/public returns at
 * least one admin-approved quote from a real (non-test) account. That is
 * deliberate: an empty carousel, skeleton quotes or "placeholder" testimonials
 * would all read as social proof that does not exist. While the list is empty
 * the homepage relies on the founder note and the product walkthrough instead.
 *
 * Only the fields the member consented to publish are available here: the quote
 * itself, the label they chose (first name, initials or "Anonymous member"), an
 * optional photo they opted into, and a date. No user id, email, program or
 * recovery detail is ever returned by that endpoint.
 */
export default function Testimonials() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/testimonials/public", { timeout: 12000 });
        if (active) setItems(Array.isArray(data?.testimonials) ? data.testimonials : []);
      } catch (err) {
        devWarn("Testimonials", err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!items.length) return null;

  return (
    <section
      data-testid="home-testimonials"
      aria-labelledby="testimonials-heading"
      className="max-w-7xl mx-auto px-4 py-14"
    >
      <h2
        id="testimonials-heading"
        className="font-display text-2xl sm:text-3xl font-bold text-[#1F2A2E]"
      >
        In members' own words
      </h2>
      <p className="mt-3 text-[#4B5A60] leading-8 max-w-3xl">
        Shared by members who asked us to publish their words, with the name option they chose.
      </p>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <figure
            key={t.id}
            data-testid={`testimonial-${t.id}`}
            className="sm-card p-6 bg-white flex flex-col gap-4"
          >
            <Quote className="w-6 h-6 text-[#B9DFE3]" aria-hidden="true" />
            <blockquote className="text-[#1F2A2E] leading-7">{t.quote}</blockquote>
            <figcaption className="mt-auto flex items-center gap-3 text-sm text-[#4B5A60]">
              {t.photo ? (
                <img
                  src={t.photo}
                  alt=""
                  className="w-9 h-9 rounded-full object-cover border border-[#E3DED4]"
                />
              ) : null}
              <span className="font-semibold">{t.label}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
